import { useState } from 'react';
import { Card, Field, Icon, Spinner } from '@/components/generic';
import { copyToClipboard } from '@/utils/string';
import { useGenerateReferralCode } from '@xhype/card-app-sdk';
import { useGetUserData } from '@package/smart-wallet/hooks';
import { useAuthStore } from '@package/smart-wallet/store';
import { useTranslation } from 'react-i18next';

const ReferralCode = () => {
  const { t } = useTranslation();
  const id = useAuthStore((store) => store.id);
  const { userInfo, isLoading, refetch } = useGetUserData();
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate referral code mutation
  const generateCode = useGenerateReferralCode({
    onSuccess: () => {
      setIsGenerating(false);
      refetch(); // Refresh user data to get new code
    },
    onError: (error: any) => {
      console.error('Error generating referral code:', error);
      setIsGenerating(false);
    },
  });

  const handleGenerateCode = async () => {
    if (!id) return;

    setIsGenerating(true);
    try {
      await generateCode.mutateAsync({ userId: id });
    } catch (error) {
      console.error('Failed to generate code:', error);
    }
  };

  // Get referral code and generate link
  const referralCode = (userInfo as any)?.referralCode || '';
  const referralLink = referralCode
    ? `${window.location.origin}/sign-up?referral=${referralCode}`
    : '';

  if (isLoading) {
    return (
      <Card title={t('profile.referral.title')} className='m-0 h-full'>
        <div className='flex items-center justify-center py-20'>
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={t('profile.referral.title')}
      className='m-0 h-full flex flex-col'
      headContent={
        !referralCode && (
          <button
            onClick={handleGenerateCode}
            disabled={isGenerating || isLoading}
            className='px-4 py-2 text-caption font-medium rounded-lg bg-primary-02 text-white hover:bg-primary-01 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2'
          >
            {isGenerating ? (
              <>
                <Spinner className='w-3 h-3' />
                Generating...
              </>
            ) : (
              'Generate Referral Code'
            )}
          </button>
        )
      }
    >
      <div className='flex flex-col gap-8 p-5 pt-0 max-lg:px-3 max-md:gap-4 flex-1 justify-center'>
        <Field
          label={t('profile.referral.link')}
          value={referralLink || 'No referral link available'}
          readOnly
          icon={
            referralLink && (
              <button
                type='button'
                onClick={() => copyToClipboard(referralLink)}
                className='absolute top-1/2 -translate-y-1/2 transition-all hover:fill-t-primary right-5 hover:scale-120'
              >
                <Icon className='w-4 h-4 fill-t-secondary' name='copy' />
              </button>
            )
          }
        />
        <Field
          label={t('profile.referral.code')}
          value={referralCode || 'No referral code available'}
          readOnly
          icon={
            referralCode && (
              <button
                type='button'
                onClick={() => copyToClipboard(referralCode)}
                className='absolute top-1/2 -translate-y-1/2 transition-all hover:fill-t-primary right-5 hover:scale-120'
              >
                <Icon className='w-4 h-4 fill-t-secondary' name='copy' />
              </button>
            )
          }
        />
      </div>
    </Card>
  );
};

export default ReferralCode;
